// client/components/SankeyGraph.tsx
// D3.js Sankey diagram for ontology flow visualization (readability-focused)
/* eslint-disable @typescript-eslint/no-explicit-any -- D3.js typing requires any for callbacks */

import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3';
import { sankey, sankeyLinkHorizontal } from 'd3-sankey';

type Node = { id: string; name: string; kind: string; meta?: any };
type Link = { source: string; target: string; value: number; meta?: any };
type Graph = { nodes: Node[]; links: Link[] };
type Props = { graph: Graph; onNodeClick: (node: Node) => void; focusNodeId?: string };

const PROVIDER_COLORS = {
  local: '#10b981',    // green
  ols: '#3b82f6',      // blue
  bioportal: '#8b5cf6',// purple
  unknown: '#6b7280',  // gray
};

const MATCH_TYPE_STYLES = {
  Exact: { strokeDasharray: 'none', opacity: 0.85 },
  Fuzzy: { strokeDasharray: '5,5', opacity: 0.6 },
  None:  { strokeDasharray: '2,2', opacity: 0.25 },
};

// --- readability helpers -----------------------------------------------------
const MIN_NODE_HEIGHT_FOR_LABEL = 8;       // px; hide labels below this, show on hover (lowered for fewer nodes)
const MAX_LABEL_CHARS = 48;                // middle-ellipsis target length

function ellipsisMid(s: string, n = MAX_LABEL_CHARS) {
  if (!s) return s;
  if (s.length <= n) return s;
  const h = Math.max(8, Math.floor((n - 1) / 2));
  const t = n - 1 - h;
  return s.slice(0, h) + '…' + s.slice(s.length - t);
}

function nodeFontSize(d: any) {
  const h = d.y1 - d.y0;
  return Math.max(12, Math.min(18, Math.floor(h / 3)));
}

export function SankeyGraph({ graph, onNodeClick, focusNodeId }: Props) {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!svgRef.current || !containerRef.current || graph.nodes.length === 0) return;

    // Clear previous render
    d3.select(svgRef.current).selectAll('*').remove();

    const container = containerRef.current;
    const width = container.clientWidth;
    const height = Math.max(600, graph.nodes.length * 16);

    // Build node index
    const nodeById = new Map(graph.nodes.map(n => [n.id, n]));

    // Convert to D3 Sankey format
    const sankeyNodes = graph.nodes.map((n, i) => ({ ...n, index: i }));
    const sankeyLinks = graph.links.map(l => ({
      source: graph.nodes.findIndex(n => n.id === l.source),
      target: graph.nodes.findIndex(n => n.id === l.target),
      value: l.value,
      meta: l.meta,
    }));

    // Layout (a bit more padding for readability)
    const sankeyLayout = sankey<any, any>()
      .nodeWidth(16)
      .nodePadding(16)
      .extent([[1, 1], [width - 1, height - 5]]);

    const { nodes, links } = sankeyLayout({ nodes: sankeyNodes, links: sankeyLinks });

    // SVG + zoom/pan group
    const svg = d3.select(svgRef.current)
      .attr('width', width)
      .attr('height', height)
      .attr('viewBox', [0, 0, width, height].join(' '))
      .attr('style', 'max-width: 100%; height: auto;');

    const root = svg.append('g').attr('class', 'sankey-root');

    const zoomed = (event: any) => { root.attr('transform', event.transform); };
    svg.call(d3.zoom<SVGSVGElement, unknown>().scaleExtent([0.6, 6]).on('zoom', zoomed));

    // Links
    root.append('g')
      .attr('class', 'links')
      .selectAll('path')
      .data(links)
      .join('path')
      .attr('d', sankeyLinkHorizontal())
      .attr('stroke', (d: any) => {
        const provider = d.meta?.provider || 'unknown';
        return PROVIDER_COLORS[provider as keyof typeof PROVIDER_COLORS] || PROVIDER_COLORS.unknown;
      })
      .attr('stroke-width', (d: any) => Math.max(1, d.width))
      .attr('stroke-dasharray', (d: any) => {
        const mt = d.meta?.matchType || 'None';
        return MATCH_TYPE_STYLES[mt as keyof typeof MATCH_TYPE_STYLES]?.strokeDasharray || 'none';
      })
      .attr('fill', 'none')
      .attr('opacity', (d: any) => {
        const mt = d.meta?.matchType || 'None';
        return MATCH_TYPE_STYLES[mt as keyof typeof MATCH_TYPE_STYLES]?.opacity ?? 0.5;
      })
      .append('title')
      .text((d: any) => {
        const s = nodeById.get(graph.nodes[d.source.index].id);
        const t = nodeById.get(graph.nodes[d.target.index].id);
        const provider = d.meta?.provider || 'unknown';
        const matchType = d.meta?.matchType || 'None';
        return `${s?.name} → ${t?.name}\nProvider: ${provider}\nMatch: ${matchType}`;
      });

    // Nodes
    const nodeGroup = root.append('g')
      .attr('class', 'nodes')
      .selectAll('g')
      .data(nodes)
      .join('g')
      .attr('class', (d: any) => `node node-${d.kind} ${focusNodeId === d.id ? 'node-focused' : ''}`)
      .style('cursor', 'pointer')
      .on('click', (event: any, d: any) => {
        event.stopPropagation();
        onNodeClick(graph.nodes[d.index]);
      });

    nodeGroup.append('rect')
      .attr('x', (d: any) => d.x0)
      .attr('y', (d: any) => d.y0)
      .attr('height', (d: any) => d.y1 - d.y0)
      .attr('width', (d: any) => d.x1 - d.x0)
      .attr('fill', (d: any) => {
        if (d.kind === 'column') return '#64748b'; // slate
        if (d.kind === 'term') {
          const provider = d.meta?._provider || 'unknown';
          return PROVIDER_COLORS[provider as keyof typeof PROVIDER_COLORS] || PROVIDER_COLORS.unknown;
        }
        if (d.kind === 'namespace') return '#f59e0b'; // amber
        return '#9ca3af'; // gray
      })
      .attr('stroke', (d: any) => (focusNodeId === d.id ? '#ef4444' : '#111'))
      .attr('stroke-opacity', 0.6)
      .attr('stroke-width', 1)
      .append('title')
      .text((d: any) => `${d.name}\n(${d.kind})`);

    // Labels (halo + dynamic size + conditional visibility)
    nodeGroup.append('text')
      .attr('x', (d: any) => (d.x0 < width / 2 ? d.x1 + 6 : d.x0 - 6))
      .attr('y', (d: any) => (d.y1 + d.y0) / 2)
      .attr('dy', '0.32em')
      .attr('text-anchor', (d: any) => (d.x0 < width / 2 ? 'start' : 'end'))
      .attr('font-size', (d: any) => nodeFontSize(d))
      .attr('fill', '#111')
      .attr('stroke', '#fff')                 // halo for contrast
      .attr('stroke-width', 3)
      .attr('paint-order', 'stroke fill')
      .style('pointer-events', 'none')        // let rect receive clicks
      .text((d: any) => ellipsisMid(d.name))
      .style('display', (d: any) => ((d.y1 - d.y0) >= MIN_NODE_HEIGHT_FOR_LABEL ? 'block' : 'none'));

    // Reveal label on hover for tiny nodes
    nodeGroup
      .on('mouseenter', function (_evt: any, d: any) {
        const textEl = d3.select(this).select('text');
        if ((d.y1 - d.y0) < MIN_NODE_HEIGHT_FOR_LABEL) {
          textEl.style('display', 'block');
        }
      })
      .on('mouseleave', function (_evt: any, d: any) {
        const textEl = d3.select(this).select('text');
        if ((d.y1 - d.y0) < MIN_NODE_HEIGHT_FOR_LABEL) {
          textEl.style('display', 'none');
        }
      });

    // Resize handler – trigger rerender by dispatching a resize event
    const handleResize = () => {
      const newWidth = containerRef.current?.clientWidth ?? width;
      if (newWidth !== width) {
        // simplest: force a React rerender by changing a key upstream
        // (kept minimal here to avoid adding state; the parent can re-mount on resize)
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [graph, onNodeClick, focusNodeId]);

  return (
    <div ref={containerRef} className="sankey-container">
      <svg ref={svgRef} className="sankey-svg" />
    </div>
  );
}
